import { Loading } from '@/components/shared';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../Buttons';
import CloseButton from '../CloseButton';
import { useConfig } from '../ConfigProvider';
import Notification from '../Notification';
import toast from '../toast';
import FileItem from './FileItem';
import { parseFileListFromString } from '@/utils/fileListParser';

const filesToArray = (files) => Object.keys(files).map((key) => files[key]);

const Upload = React.forwardRef((props, ref) => {
  const { accept, beforeUpload, disabled, draggable, fileList, multiple, onChange, onFileRemove, showList, tip, uploadLimit, children, className, field, form, ocrLoading = false, ...rest } = props;

  const fileInputField = useRef(null);
  // Parse fileList in case it comes as a stringified array from API
  const [files, setFiles] = useState(() => {
    const parsedFileList = parseFileListFromString(fileList);
    return Array.isArray(parsedFileList) ? parsedFileList : [];
  });
  const [dragOver, setDragOver] = useState(false);

  const { themeColor, primaryColorLevel } = useConfig();

  useEffect(() => {
    // Parse fileList whenever it changes
    const parsedFileList = parseFileListFromString(fileList);
    setFiles(Array.isArray(parsedFileList) ? parsedFileList : []);
  }, [fileList]);

  const triggerMessage = (msg) => {
    toast.push(
      <Notification type="danger" duration={2000}>
        {msg || 'Upload Failed!'}
      </Notification>,
      {
        placement: 'top-center',
      }
    );
  };

  const pushFile = (newFiles, file) => {
    for (let f of newFiles) {
      file.push(f);
    }
    return file;
  };

  const addNewFiles = (newFiles) => {
    // Ensure files is always an array
    let file = cloneDeep(Array.isArray(files) ? files : []);

    if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
      if (file.length >= uploadLimit) {
        if (uploadLimit === 1) {
          file.shift();
          file = pushFile(newFiles, file);
        } else {
          // If we've reached the limit and it's not 1, don't add more files
          return file;
        }
        return file;
      }
    }
    file = pushFile(newFiles, file);
    return file;
  };

  const onNewFileUpload = (e) => {
    const { files: newFiles } = e.target;
    let result = true;

    if (beforeUpload) {
      result = beforeUpload(newFiles, files);

      if (result === false) {
        triggerMessage();
        return;
      }

      if (typeof result === 'string' && result.length > 0) {
        triggerMessage(result);
        return;
      }
    }

    if (result) {
      let updatedFiles = addNewFiles(newFiles);
      setFiles(updatedFiles);
      onChange?.(updatedFiles, files);
    }
  };

  const removeFile = (fileIndex) => {
    // Ensure files is an array before filtering
    const currentFiles = Array.isArray(files) ? files : [];
    const deletedFileList = currentFiles.filter((_, index) => index !== fileIndex);
    setFiles(deletedFileList);
    onFileRemove?.(deletedFileList);
  };

  const triggerUpload = (e) => {
    if (!disabled) {
      fileInputField.current?.click();
    }
    e.stopPropagation();
  };

  const renderChildren = () => {
    if (!draggable && !children) {
      return (
        <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
          Upload
        </Button>
      );
    }

    if (draggable && !children) {
      return <span>Choose a file or drag and drop here</span>;
    }

    return children;
  };

  const handleDragLeave = useCallback(() => {
    if (draggable) {
      setDragOver(false);
    }
  }, [draggable]);

  const handleDragOver = useCallback(() => {
    if (draggable && !disabled) {
      setDragOver(true);
    }
  }, [draggable, disabled]);

  const handleDrop = useCallback(() => {
    if (draggable) {
      setDragOver(false);
    }
  }, [draggable]);

  const draggableProp = {
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  };

  const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`;

  const uploadClass = classNames('upload', draggable && `upload-draggable`, draggable && !disabled && `hover:${draggableEventFeedbackClass}`, draggable && disabled && 'disabled', dragOver && draggableEventFeedbackClass, className);

  const uploadInputClass = classNames('upload-input', draggable && `draggable`);

  console.log("Files in Upload Component:", files);

  // Ensure files is an array before rendering
  const safeFiles = Array.isArray(files) ? files : [];

  return (
    <>
      <div ref={ref} className={uploadClass} {...(draggable ? draggableProp : { onClick: triggerUpload })} {...rest}>
        <input className={uploadInputClass} type="file" ref={fileInputField} onChange={onNewFileUpload} disabled={disabled} multiple={multiple} accept={accept} title="" value="" {...field} {...rest}></input>

        <div className="flex items-center">
          {renderChildren()}
          <Loading loading={ocrLoading}></Loading>
        </div>
      </div>
      {tip}
      {showList && (
        <div className="upload-file-list">
          {safeFiles.map((file, index) => (
            <FileItem file={file} key={`${file.name}-${index}`}>
              <CloseButton onClick={() => removeFile(index)} className="upload-file-remove" />
            </FileItem>
          ))}
        </div>
      )}
    </>
  );
});

Upload.propTypes = {
  uploadLimit: PropTypes.number,
  draggable: PropTypes.bool,
  disabled: PropTypes.bool,
  showList: PropTypes.bool,
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  fileList: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  ocrLoading: PropTypes.bool,
};

Upload.defaultProps = {
  draggable: false,
  showList: true,
  disabled: false,
  fileList: [],
  ocrLoading: false,
};

export default Upload;
